import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';
import { searchPlants } from '../lib/plant_search';
import Store from './store';
import Editor from './editor';
import Renderer from './canvas/renderer';
import Interactions from './canvas/interactions';
import SaveCoordinator from './persistence/save_coordinator';
import { PlanLoadError } from './data';
import { renderPanels, handlePanelChange, handlePanelClick, handlePanelInput, handlePanelKeydown, handlePanelFocusOut, reorderLabels, reorderLabelKeys } from './panels';
import { searchResultsHtml } from './panels/search';
import { esc, elementLabel } from './panels/html';
import { toContent } from './serialize';
import { makePlantElement, speciesLayer } from './plants';
import { DEFAULT_LAYERS, migrate } from './migrations';
import { dateRange, monthLabel, todayMonth } from './dates';
import { offsetElement, elementRect, contentOffscreen } from './model/bounds';
import {
  deleteElements, addElements, nudgeElements, reorderElements, scalePlan, scaleImages,
  setImagesVisible, unlockedIds,
} from './model/mutators/elements';
import { reorderLayers } from './model/mutators/layers';
import { addSpecies } from './model/mutators/labels';
import { pasteStyle } from './model/mutators/props';
import { smoothPath, straightenPath, smoothSegment, straightenSegment, removeVertex, setSegmentType } from './model/mutators/paths';
import { nearestSegment } from './model/segments';
import { encodeElements, decodeElements } from './model/clipboard';
import { setSetting } from './model/mutators/settings';
import { isDrawTool, defaultDrawStyles, drawStyleData, placementStyles } from './model/draw_style';
import { gridSpacing } from './model/grid';
import { plantElements } from './panels/plants';
import { plannerSignInUrl } from '../lib/planner_auth';
import { parseSaveErrorBody } from '../lib/plan_save_errors';
import { IMAGE_ACCEPT, IMAGE_FORMATS_HINT, capturePlannerImageUpload } from '../lib/image_upload';

const SEARCH_PAGE = 30;

// A new plan nudges the user to save once they've built up enough to lose —
// v1 parity (NEW_PLAN_SAVE_PROMPT_THRESHOLD).
const NEW_PLAN_SAVE_THRESHOLD = 3;

// Root controller for the v2 planner. Owns store + Konva renderer + interactions
// and orchestrates mutations (undo, autosave, dirty-state).
export default class extends Controller {
  static values = {
    dataUrl: String, saveUrl: String, imageUrl: String, username: String, slug: String,
    new: Boolean, createUrl: String, preloadPlant: String, viewOnly: Boolean,
    owner: String, description: String,
  };
  static targets = ['canvas', 'loading', 'save', 'undo', 'redo', 'zoom', 'zoomMenu', 'jump', 'tabs', 'timeline', 'scalebar', 'snap', 'searchInput', 'searchResults', 'tipbar'];

  connect() {
    this.store = new Store();
    this.store.state.drawStyles = defaultDrawStyles(); // session-only, see model/draw_style
    this.editor = new Editor(this.store, {
      onDirty: () => this.markDirty(),
      onHistoryChange: () => this.updateUndoButtons(),
    });
    this.saveCoordinator = this.buildSaveCoordinator();
    this.renderer = new Renderer(this.canvasTarget, this.store);
    this.interactions = new Interactions(this.renderer, this.store, this);
    this.interactions.bind();
    this.renderer.setDraggable(!this.viewOnlyValue); // view-only: nothing draggable
    if (this.viewOnlyValue) this.store.set({ activeTool: 'navigate', activeTab: 'plants' });
    this.canvasTarget.dataset.tool = this.store.state.activeTool; // per-tool cursor (CSS)
    this._hits = [];

    this.unsub = this.store.subscribe((state) => {
      // A menu opened on a selection goes when the selection is cleared.
      if (this._ctxMenu && this._ctxMenuSelected && !state.selection.length) this.closeContextMenu();
      this.syncChrome(state);
      this.refreshPanels(state);
    });
    // Esc closes an open context menu and does nothing else (edit and view
    // mode alike) — captured ahead of the planner's own Esc handling.
    this._onMenuKey = (e) => {
      if (e.key !== 'Escape' || !this._ctxMenu) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.closeContextMenu();
    };
    window.addEventListener('keydown', this._onMenuKey, true);
    if (!this.viewOnlyValue) {
      this._onKey = (e) => this.onKey(e);
      this._onKeyUp = (e) => this.onKeyUp(e);
      window.addEventListener('keydown', this._onKey);
      window.addEventListener('keyup', this._onKeyUp);
      // New plans are not autosaved, so warn before the tab closes with unsaved
      // work. Controlled navigation persists a draft first and bypasses this.
      this._onBeforeUnload = (e) => {
        if (this.store.state.dirty && !this._allowPageExit) {
          this.saveCoordinator.persistDraft();
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', this._onBeforeUnload);
    } else {
      // View mode has no shortcuts, but Esc still closes a modal (settings).
      this._onKey = (e) => {
        if (e.key !== 'Escape') return;
        const modal = [...this.element.querySelectorAll('.modal-overlay:not([hidden])')].pop();
        if (modal) { e.preventDefault(); modal.dispatchEvent(new MouseEvent('mousedown', { bubbles: true })); }
      };
      window.addEventListener('keydown', this._onKey);
    }
    // The fullscreen button flips to "exit" while fullscreen — however it was
    // entered or left (the button, Esc, the browser's own controls).
    this._onFullscreen = () => this.syncFullscreenButton();
    document.addEventListener('fullscreenchange', this._onFullscreen);
    this.element.addEventListener('change', (e) => handlePanelChange(e, this));
    this.element.addEventListener('input', (e) => handlePanelInput(e, this));
    this.element.addEventListener('keydown', (e) => handlePanelKeydown(e, this));
    // What a press landed on, for the change/focusout it causes (a button that
    // doesn't take focus leaves relatedTarget empty). Only while it's down.
    this.element.addEventListener('pointerdown', (e) => { this._pressTarget = e.target; }, true);
    this.element.addEventListener('pointerup', () => { this._pressTarget = null; }, true);
    this.element.addEventListener('focusout', (e) => handlePanelFocusOut(e, this));
    this.element.addEventListener('click', (e) => this.onPanelClick(e));
    this._onHover = (e) => { this.onSpeciesHover(e); this.onTipHover(e); this.onRowHover(e); };
    this.element.addEventListener('mouseover', this._onHover);
    this.element.addEventListener('mouseout', (e) => {
      if (e.target.closest?.('[data-species-info]')) this.hideSpeciesFlyout();
      if (e.target.closest?.('[data-tip]')) this.hideTip();
      // Leaving the planner from a row fires no further mouseover inside it.
      if (this._rowHover && !this.element.contains(e.relatedTarget)) { this._rowHover = false; this.renderer.setHover(null); }
    });

    window.planner2 = { store: this.store, editor: this.editor, renderer: this.renderer, saveCoordinator: this.saveCoordinator, controller: this, serialize: () => toContent(this.store.state) };
    if (this.newValue) this.initNewPlan(); else this.load();
  }

  // A live view over the Stimulus values (getters, plus setters for the fields
  // a rename can change), so the coordinator and the controller always agree on
  // the plan's current identity without duplicating state.
  buildSaveCoordinator() {
    const controller = this;
    const config = {
      get username() { return controller.usernameValue; },
      get slug() { return controller.slugValue; },
      set slug(v) { controller.slugValue = v; },
      get isNew() { return controller.newValue; },
      get saveUrl() { return controller.saveUrlValue; },
      set saveUrl(v) { controller.saveUrlValue = v; },
      get dataUrl() { return controller.dataUrlValue; },
      set dataUrl(v) { controller.dataUrlValue = v; },
      get imageUrl() { return controller.imageUrlValue; },
      set imageUrl(v) { controller.imageUrlValue = v; },
      get createUrl() { return controller.createUrlValue; },
    };
    return new SaveCoordinator(this.store, config, {
      onDirtyChange: (dirty) => { this.saveTarget?.classList.toggle('is-dirty', dirty); },
      onSaveError: (e, url) => {
        this.showError(e.message, e.sessionExpired);
        this.reportSaveError(e, url);
      },
      onDraftPersistFailed: () => this.showDraftPersistenceError(),
      promptName: () => this.promptName(),
      promptGuestSignIn: () => this.promptGuestSignIn(),
      onRenamed: (base) => { try { window.history.replaceState({}, '', `${base}/edit-v2`); } catch (_) { /* cross-origin guard */ } },
      getSnapshotBlob: () => this.renderer.snapshotBlob(),
      onCreated: (uri) => { window.location.href = String(uri || '').replace(/\/edit$/, '/edit-v2'); },
    });
  }

  // Start an empty plan (no server fetch). First save POSTs to #create.
  initNewPlan() {
    this.store.loadPlan({
      version: 4, name: null,
      view: { scale: 1, posX: 0, posY: 0 },
      settings: { name: null, access: 'public', showGrid: true, snapToGrid: true, showLabels: true, units: 'metric', autoSave: true },
      layers: DEFAULT_LAYERS, elements: [], species: {},
    });
    this.renderer.fitToContent();
    this.applySettings();
    if (this.hasLoadingTarget) this.loadingTarget.remove();
    if (this.preloadPlantValue) {
      try { this.startPlacement(JSON.parse(this.preloadPlantValue)); } catch (_) { /* ignore */ }
    }
    this.maybeRestoreDraft();
    this.maybeWelcome();
  }

  disconnect() {
    this.closeContextMenu();
    window.removeEventListener('keydown', this._onKey);
    window.removeEventListener('keydown', this._onMenuKey, true);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    document.removeEventListener('fullscreenchange', this._onFullscreen);
    this.saveCoordinator.cancelTimers();
    this._layerSort?.destroy();
    this.hideSpeciesFlyout();
    this.hideTip();
    this.clearHint();
    this.unsub?.();
    this.renderer?.destroy();
    delete window.planner2;
  }

  async load() {
    try {
      const plan = await this.saveCoordinator.loadPlan();
      this.store.loadPlan(plan);
      if (!(plan.view && plan.view.scale)) this.renderer.fitToContent();
      this.applySettings();
      if (this.hasLoadingTarget) this.loadingTarget.remove();
      if (plan.showMigrationMessage) this.showMigrationBanner();
      this.maybeRestoreDraft();
      this.checkStrays();
      this.maybeWelcome();
    } catch (e) {
      this.showLoadError();
      this.reportLoadError(e);
    }
  }

  // Report a load failure under a stable exception name so every failure groups
  // into one issue, with the status as an event property — instead of a raw,
  // engine-worded body-read exception that opens a fresh issue per browser. A
  // transport failure (a fetch that never landed, or a body the browser could
  // not finish reading) or an expired session (401) is a transient, non-code
  // problem: the user still sees the retry panel and keeps their plan on the
  // server, so neither is filed as a bug.
  reportLoadError(e) {
    if (e.isNetworkError || e.status === 401) return;
    if (e instanceof PlanLoadError) {
      window.posthog?.captureException?.(new Error('Plan data load failed'), {
        load_status: e.status,
        load_url: this.dataUrlValue,
      });
      return;
    }
    // A genuine data or render error further down load() is a real bug — report it as-is.
    window.posthog?.captureException?.(e);
  }

  // A transient load failure (flaky network) shouldn't strand the user on a
  // dead page — offer a retry that re-runs the whole load.
  showLoadError() {
    if (!this.hasLoadingTarget) return;
    this.loadingTarget.innerHTML = `
      <p>We could not load this plan. Check your connection, then try again.</p>
      <button class="pl2-btn" type="button" data-action="planner#retryLoad">Retry</button>`;
  }

  retryLoad() {
    if (this.hasLoadingTarget) this.loadingTarget.textContent = 'Loading plan…';
    this.load();
  }

  // If a localStorage draft exists and differs from what just loaded, offer to
  // restore it via a non-blocking banner (drafts are written on every edit). A
  // same-tab sign-in handoff is offered first, then the account's own draft.
  maybeRestoreDraft() {
    const found = this.saveCoordinator.findDraft();
    if (found) this.showDraftBanner(found);
  }

  showDraftBanner(found) {
    this.element.querySelector('.pl2-draft-banner')?.remove();
    const bar = document.createElement('div');
    bar.className = 'pl2-draft-banner';
    bar.innerHTML = `
      <span>Unsaved changes from a previous session.</span>
      <button class="pl2-btn" type="button" data-draft="restore">Restore</button>
      <button class="btn-clear-light" type="button" data-draft="discard">Discard</button>`;
    bar.querySelector('[data-draft="restore"]').addEventListener('click', () => {
      if (!this.saveCoordinator.acceptDraft(found)) return; // the error modal explains
      this.restoreDraft(found.draft);
      bar.remove();
    });
    bar.querySelector('[data-draft="discard"]').addEventListener('click', () => {
      const next = this.saveCoordinator.discardDraft(found);
      bar.remove();
      if (next) this.showDraftBanner(next);
    });
    this.element.appendChild(bar);
  }

  restoreDraft(draft) {
    this.store.loadPlan(migrate(draft));
    this.applySettings();
    this.markDirty();
  }

  // An older plan was upgraded to the current format in memory — say so, since
  // nothing is written back until the next save (v1 showed the same notice).
  showMigrationBanner() {
    if (this.viewOnlyValue) return;
    this.element.querySelector('.pl2-draft-banner')?.remove();
    const bar = document.createElement('div');
    bar.className = 'pl2-draft-banner pl2-migration-banner';
    bar.innerHTML = `
      <span>This plan was made with an older version of the planner and has been updated. Save it to keep the new format.</span>
      <button class="pl2-btn" type="button" data-migration="save">Save now</button>
      <button class="pl2-iconbtn" type="button" data-migration="dismiss" title="Dismiss">×</button>`;
    bar.querySelector('[data-migration="save"]').addEventListener('click', () => { this.save(); bar.remove(); });
    bar.querySelector('[data-migration="dismiss"]').addEventListener('click', () => bar.remove());
    this.element.appendChild(bar);
  }

  // Warn when the plan has stray objects far from everything else (they distort
  // the view). Offer to reveal them or delete them. View-only plans just inform.
  checkStrays() {
    if (this.viewOnlyValue) return;
    const ids = this.renderer.strayElementIds(this.store.state);
    if (ids.length) this.showStrayBanner(ids);
  }

  showStrayBanner(ids) {
    this.element.querySelector('.pl2-stray-banner')?.remove();
    const n = ids.length;
    const bar = document.createElement('div');
    bar.className = 'pl2-stray-banner';
    bar.innerHTML = `
      <span>⚠ ${n} object${n > 1 ? 's' : ''} sit far from the rest of your plan and can distort the view.</span>
      <button class="pl2-btn" type="button" data-stray="show">Show</button>
      <button class="pl2-danger-btn pl2-btn" type="button" data-stray="delete">Delete ${n > 1 ? 'them' : 'it'}</button>
      <button class="pl2-iconbtn" type="button" data-stray="dismiss" title="Dismiss">×</button>`;
    bar.querySelector('[data-stray="show"]').addEventListener('click', () => {
      this.store.set({ selection: unlockedIds(this.store.state, ids) });
      this.renderer.fitAll();
    });
    bar.querySelector('[data-stray="delete"]').addEventListener('click', () => {
      this.editor.apply(deleteElements, { ids });
      this.renderer.fitToContent();
      bar.remove();
    });
    bar.querySelector('[data-stray="dismiss"]').addEventListener('click', () => bar.remove());
    this.element.appendChild(bar);
  }

  // Drag-to-reorder the layers list (rebuilt on every render, so re-init each time).
  initLayerSort(list) {
    if (!list || this.viewOnlyValue) return;
    this._layerSort?.destroy();
    this._layerSort = Sortable.create(list, {
      handle: '[data-drag-handle]',
      draggable: '.pl2-row',
      animation: 120,
      onEnd: () => this.reorderLayersFromDom(list),
    });
  }

  // Drag-to-reorder labels (drag anywhere on an inert row) and a species
  // label's options, in the Properties panel. Rebuilt with the panel.
  initLabelSort(panel) {
    for (const sort of this._labelSorts || []) sort.destroy();
    this._labelSorts = [];
    const list = panel.querySelector('.pl2-label-list');
    if (!list) return;
    this._labelSorts.push(Sortable.create(list, {
      draggable: '.pl2-label-row',
      filter: 'input, button, .pl2-keyoptions',
      preventOnFilter: false,
      animation: 120,
      onEnd: (e) => reorderLabels(this, list.dataset.elId, e.oldDraggableIndex, e.newDraggableIndex),
    }));
    for (const options of panel.querySelectorAll('.pl2-keyoptions')) {
      this._labelSorts.push(Sortable.create(options, {
        draggable: '.pl2-keyoption',
        filter: 'input',
        preventOnFilter: false,
        animation: 120,
        onEnd: (e) => { if (e.oldIndex !== e.newIndex) reorderLabelKeys(this, options); },
      }));
    }
  }

  reorderLayersFromDom(list) {
    const ids = Array.from(list.querySelectorAll('[data-layer-id]')).map((r) => r.dataset.layerId);
    this.editor.apply(reorderLayers, { ids });
  }

  // --- mutation plumbing ----------------------------------------------------
  // The pipeline lives in editor.js; these delegates keep the gesture-caller and
  // system-test API stable.
  takeSnapshot() { return this.editor.takeSnapshot(); }
  commit(prevSnapshot, fn) { this.editor.commitGesture(prevSnapshot, fn); }
  get undoStack() { return this.editor.undoStack; }

  markDirty() {
    if (this.viewOnlyValue) return; // local exploration (layer toggles) is never saved
    this.saveCoordinator.markDirty();
    this.maybeAskToSave();
  }

  save() { return this.saveCoordinator.save(); }

  // New-plan nudge (v1 parity): once a guest-or-not builds up a few elements on
  // an unsaved plan, ask once whether they'd like to save it.
  maybeAskToSave() {
    if (!this.newValue || this._askedToSave) return;
    if (this.store.state.elements.length < NEW_PLAN_SAVE_THRESHOLD) return;
    this._askedToSave = true;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Save your plan?</h3></header>
        <section class="modal-content"><p>Your plan is coming along — save it so it's safe and you can come back to it.</p></section>
        <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Not now</button><button class="btn-primary" data-act="ok" type="button">Save</button></div>
      </div>`;
    overlay.querySelector('[data-act="ok"]').addEventListener('click', () => { overlay.remove(); this.save(); });
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) overlay.remove(); });
    this.element.appendChild(overlay);
  }

  // Guests can build a plan but need an account to persist it. Rather than
  // bouncing them straight to the sign-in page (which reads as a dead end),
  // explain what's happening and reassure them their work is kept. The draft is
  // already stored under the guest key; "Sign in to save" also hands a
  // token-scoped copy to this tab, which the signed-in page offers to restore.
  promptGuestSignIn() {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Save your garden plan</h3></header>
        <section class="modal-content"><p>Create a free account or sign in to save your plan. Your work is kept while you do — it'll be here when you get back.</p></section>
        <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Keep editing</button><button class="btn-primary" data-act="ok" type="button">Sign in to save</button></div>
      </div>`;
    overlay.querySelector('[data-act="ok"]').addEventListener('click', () => {
      if (!this.saveCoordinator.startSignInHandoff()) return; // the error modal explains
      this._allowPageExit = true;
      window.location.href = plannerSignInUrl(window.location.href);
    });
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) overlay.remove(); });
    this.element.appendChild(overlay);
  }

  // Leave the page (or reload it) only once the latest edits are safely in the
  // local draft; the beforeunload warning is bypassed for this exit.
  persistDraftForPageExit() {
    this._allowPageExit = false;
    if (!this.saveCoordinator.persistDraft()) return false;
    this._allowPageExit = true;
    return true;
  }

  navigateWithDraft(url) {
    if (!this.persistDraftForPageExit()) return false;
    window.location.href = url;
    return true;
  }

  refreshWithDraft() {
    if (!this.persistDraftForPageExit()) return false;
    window.location.reload();
    return true;
  }

  showDraftPersistenceError() {
    this.showError('We could not store your latest changes in this browser. Keep this page open, free some browser storage, and try again.');
  }

  // Report a save failure under a stable exception name so every failure groups
  // into one issue, with the detail as event properties. A network-level
  // rejection or an expired page session is a transient, non-code problem (the
  // user still sees an error and keeps their local draft), so neither is
  // reported as a code bug.
  reportSaveError(e, url) {
    if (e.isNetworkError || e.sessionExpired) return;
    window.posthog?.captureException?.(new Error('Plan save failed'), {
      save_status: e.status,
      save_message: e.message,
      save_url: url,
    });
  }

  // Simple name prompt modal; resolves to the entered name or null if cancelled.
  promptName() {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.display = 'flex';
      overlay.innerHTML = `
        <div class="modal-container">
          <header class="modal-header"><h3>Name your plan</h3></header>
          <section class="modal-content"><input class="input-text pl2-input" type="text" placeholder="Garden plan" style="width:100%"></section>
          <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Cancel</button><button class="btn-primary" data-act="ok" type="button">Save</button></div>
        </div>`;
      const input = overlay.querySelector('input');
      const done = (val) => { overlay.remove(); resolve(val); };
      overlay.querySelector('[data-act="ok"]').addEventListener('click', () => done(input.value.trim() || 'Untitled plan'));
      overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => done(null));
      overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(null); });
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') done(input.value.trim() || 'Untitled plan'); if (e.key === 'Escape') done(null); });
      this.element.appendChild(overlay);
      input.focus();
    });
  }

  undo() { this.editor.undo(); }
  redo() { this.editor.redo(); }

  updateUndoButtons() {
    if (this.hasUndoTarget) this.undoTarget.disabled = !this.editor.canUndo();
    if (this.hasRedoTarget) this.redoTarget.disabled = !this.editor.canRedo();
  }

  // --- toolbar / tabs -------------------------------------------------------
  setTool(event) {
    this.applyTool(event.currentTarget.dataset.tool, 'user');
  }

  // `source` is 'user' for a toolbar click or keyboard shortcut, and unset for
  // the internal auto-switches (back to select, hold-Space pan), so only real
  // picks are tracked.
  applyTool(tool, source) {
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    this.clearHint();
    if (tool !== 'copystyle') this.pickedStyle = null;
    const keepPending = tool === 'placement' || tool === 'addspecies';
    // Picking a draw tool starts fresh: nothing selected, and Properties shows
    // what the next shape will be drawn with.
    const drawing = isDrawTool(tool);
    // Before the store update: its reconcile syncs the transformer, which only
    // attaches to the selection while nodes are draggable (a freshly drawn
    // shape is already selected when finishing switches back to select).
    this.renderer.setDraggable(tool === 'select');
    this.store.set({
      activeTool: tool,
      pendingSpecies: keepPending ? this.store.state.pendingSpecies : null,
      ...(drawing ? { selection: [] } : {}),
    });
    if (drawing && !this.viewOnlyValue) this.showTab('properties');
    if (tool === 'navigate' || drawing) this.renderer.setHover(null);
    this.canvasTarget.dataset.tool = tool; // per-tool cursor (CSS)
    this.syncToolButtons();
    if (source === 'user') window.posthog?.capture('planner_tool_selected', { tool, planner_version: 'v2' });
  }

  syncToolButtons() {
    const tool = this.store.state.activeTool;
    this.element.querySelectorAll('.pl2-tool[data-tool]').forEach((btn) => {
      const active = btn.dataset.tool === tool;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  setTab(event) { this.showTab(event.currentTarget.dataset.tab); }

  showTab(tab) {
    this.store.set({ activeTab: tab });
    this.element.querySelectorAll('.pl2-tab[data-tab]').forEach((b) => b.classList.toggle('is-active', b.dataset.tab === tab));
    this.element.querySelectorAll('[data-tab-panel]').forEach((p) => { p.hidden = p.dataset.tabPanel !== tab; });
    if (tab === 'lists') this.loadLists();
  }

  // --- zoom ----------------------------------------------------------------
  resetZoom() { this.renderer.fitToContent(); }

  zoomCenter() { return { x: this.renderer.stage.width() / 2, y: this.renderer.stage.height() / 2 }; }

  zoomToScaleAt(scale, pt) {
    const v = this.store.state.view; const old = v.scale || 1;
    const clamped = Math.min(50, Math.max(0.02, scale));
    const mx = (pt.x - (v.posX || 0)) / old;
    const my = (pt.y - (v.posY || 0)) / old;
    this.store.set({ view: { scale: clamped, posX: pt.x - mx * clamped, posY: pt.y - my * clamped } }, { source: 'zoom' });
  }

  zoomIn() { this.zoomToScaleAt((this.store.state.view.scale || 1) * 1.2, this.zoomCenter()); }
  zoomOut() { this.zoomToScaleAt((this.store.state.view.scale || 1) / 1.2, this.zoomCenter()); }
  zoom25() { this.zoomToScaleAt(0.25, this.zoomCenter()); this.closeZoomMenu(); }
  zoom50() { this.zoomToScaleAt(0.5, this.zoomCenter()); this.closeZoomMenu(); }
  zoom100() { this.zoomToScaleAt(1, this.zoomCenter()); this.closeZoomMenu(); }
  zoom200() { this.zoomToScaleAt(2, this.zoomCenter()); this.closeZoomMenu(); }
  zoomFit() { this.renderer.fitToContent(); this.closeZoomMenu(); }
  toggleZoomMenu() { if (this.hasZoomMenuTarget) this.zoomMenuTarget.hidden = !this.zoomMenuTarget.hidden; }
  closeZoomMenu() { if (this.hasZoomMenuTarget) this.zoomMenuTarget.hidden = true; }

  // Mobile: the left panel slides in/out as an overlay (CSS-driven).
  togglePanel() { this.element.classList.toggle('pl2-panel-open'); }
  closePanel() { this.element.classList.remove('pl2-panel-open'); }

  // --- image elements ------------------------------------------------------
  // Images are ordinary elements: select one to move/resize/rotate it on the
  // canvas and edit it in Properties; lock it (Elements tab) to trace over it.
  openImagePanel() {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    const hasImage = this.store.state.elements.some((el) => el.type === 'image');
    const body = this.newValue || !this.imageUrlValue
      ? '<p class="pl2-muted">Save the plan before you add an image.</p>'
      : `<p class="pl2-muted">Add one or more satellite, sketch, or reference images.</p>
         <label class="pl2-btn" style="display:inline-flex">Choose images…<input type="file" accept="${IMAGE_ACCEPT}" data-img="file" multiple hidden></label>
         <p class="pl2-muted">${IMAGE_FORMATS_HINT}</p>
         ${hasImage ? '<button class="pl2-btn" type="button" data-img="calibrate" style="margin-top:8px;display:block">Set scale (calibrate)…</button>' : ''}`;
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Add images</h3></header>
        <section class="modal-content">${body}</section>
        <div class="modal-footer"><button class="btn-primary" type="button" data-act="close">Done</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-img="file"]')?.addEventListener('change', async (e) => {
      for (const file of Array.from(e.target.files || [])) await this.uploadImage(file);
      e.target.value = '';
    });
    overlay.querySelector('[data-img="calibrate"]')?.addEventListener('click', () => { this.startCalibration(); close(); });
    this.element.appendChild(overlay);
  }

  // Scale calibration: click two points a known real-world distance apart, then
  // the whole plan (or just its images) is scaled so plan units (cm) read true.
  startCalibration() {
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    this.interactions._calib = [];
    this.store.set({ activeTool: 'calibrate' });
    this.renderer.setDraggable(false);
    this.canvasTarget.dataset.tool = 'calibrate';
    this.syncToolButtons();
    this.showHint('Click two points a known distance apart (Esc to cancel).');
  }

  async finishCalibration(a, b) {
    const measured = Math.hypot(b.x - a.x, b.y - a.y);
    this.applyTool('select');
    this.clearHint();
    if (measured < 1) return;
    const imperial = this.store.state.settings.units === 'imperial';
    const answer = await this.promptCalibration(imperial);
    if (answer == null) return;
    const real = parseFloat(answer.value);
    if (!real || real <= 0) return;
    const factor = (imperial ? real * 30.48 : real * 100) / measured;
    if (answer.imageOnly) {
      // Fit the photos to a plan that's already drawn to scale — the drawing
      // must not move (v1's image calibration).
      this.editor.apply(scaleImages, { factor, origin: a });
    } else {
      this.editor.apply(scalePlan, { factor, origin: a });
      this.renderer.fitToContent();
    }
  }

  // Distance prompt for calibration, with the choice of rescaling the whole
  // plan or just its images (only offered when there are some).
  promptCalibration(imperial) {
    const hasImage = this.store.state.elements.some((el) => el.type === 'image');
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.display = 'flex';
      overlay.innerHTML = `
        <div class="modal-container">
          <header class="modal-header"><h3>Set the scale</h3></header>
          <section class="modal-content">
            <div class="pl2-field"><label>Real distance between the two points (${imperial ? 'feet' : 'metres'})</label>
              <input class="input-text pl2-input" type="number" min="0" step="any" data-cal="value" style="width:100%"></div>
            ${hasImage ? `<label class="pl2-check" style="margin-top:8px">
              <input type="checkbox" data-cal="image-only"> Resize the images only (leave the drawing as it is)
            </label>` : ''}
          </section>
          <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Cancel</button><button class="btn-primary" data-act="ok" type="button">Apply</button></div>
        </div>`;
      const input = overlay.querySelector('[data-cal="value"]');
      const imageOnly = overlay.querySelector('[data-cal="image-only"]');
      const done = (result) => { overlay.remove(); resolve(result); };
      const submit = () => {
        const value = input.value.trim();
        done(value ? { value, imageOnly: !!imageOnly?.checked } : null);
      };
      overlay.querySelector('[data-act="ok"]').addEventListener('click', submit);
      overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => done(null));
      overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(null); });
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') done(null); });
      this.element.appendChild(overlay);
      input.focus();
    });
  }

  showHint(msg) {
    this.clearHint();
    const el = document.createElement('div');
    el.className = 'pl2-hint';
    el.textContent = msg;
    this.element.appendChild(el);
    this._hint = el;
  }

  clearHint() { this._hint?.remove(); this._hint = null; }

  // A hint that clears itself — for row-action feedback rather than tool modes.
  flashHint(msg, ms = 2500) {
    this.showHint(msg);
    clearTimeout(this._flashT);
    this._flashT = setTimeout(() => this.clearHint(), ms);
  }

  async uploadImage(file) {
    const form = new FormData();
    form.append('image[file]', file);
    try {
      const res = await fetch(`${this.imageUrlValue}/planner_image`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': this.csrf(), Accept: 'application/json' },
        body: form,
      });
      if (!res.ok) {
        // Show the server's validation message (wrong format or too large)
        // rather than a bare HTTP status code.
        const body = await res.json().catch(() => null);
        const { message } = parseSaveErrorBody(body, res.status);
        this.captureImageUpload('rejected', file, res.status);
        this.showError(message, false, 'We could not add that image');
        return;
      }
      const data = await res.json();
      this.captureImageUpload('success', file, res.status);
      const el = {
        id: `image-${data.id}`,
        type: 'image',
        speciesIds: [],
        data: {
          imageId: data.id, name: data.filename || file.name, url: data.url,
          x: 0, y: 0, width: null, height: null, rotation: 0, opacity: 200,
          visible: true, locked: false,
          layer: null, addedDate: null, removedDate: null, labels: [], labelX: 0, labelY: 0,
        },
      };
      this.applyTool('select');
      this.editor.apply(addElements, { elements: [el] });
    } catch (e) {
      this.captureImageUpload('error', file);
      this.showError(e.message, false, 'We could not add that image');
    }
  }

  captureImageUpload(result, file, status = null) {
    capturePlannerImageUpload({
      plannerVersion: 'v2',
      result,
      fileType: file?.type,
      fileSizeBytes: file?.size,
      status,
    });
  }

  csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || ''; }

  // --- timeline ------------------------------------------------------------
  toggleTimeline() {
    const on = !this.store.state.timelineOn;
    const range = dateRange(this.store.state.elements);
    let month = this.store.state.timelineMonth;
    if (on && month == null) month = range ? range.max : todayMonth();
    this.store.set({ timelineOn: on, timelineMonth: month });
    if (this.hasTimelineTarget) this.timelineTarget.hidden = !on;
    this.renderTimeline();
  }

  renderTimeline() {
    if (!this.hasTimelineTarget || !this.store.state.timelineOn) return;
    const t = todayMonth() ?? 24300;
    const range = dateRange(this.store.state.elements) || { min: t - 12, max: t + 12 };
    const month = this.store.state.timelineMonth ?? range.max;
    this.timelineTarget.innerHTML = `
      <span class="pl2-timeline-icon">${'●'}</span>
      <input class="pl2-timeline-range" type="range" min="${range.min}" max="${range.max}" step="1" value="${month}" data-action="input->planner#onTimelineInput">
      <span class="pl2-timeline-label">${monthLabel(month)}</span>
      <button class="pl2-btn pl2-timeline-today" type="button" data-action="planner#timelineToday">Today</button>
      <button class="pl2-tool pl2-zoom-step" type="button" data-action="planner#toggleTimeline" title="Close timeline">${'×'}</button>`;
  }

  onTimelineInput(e) {
    const month = Number(e.target.value);
    this.store.set({ timelineMonth: month }, { source: 'timeline' });
    const lbl = this.timelineTarget.querySelector('.pl2-timeline-label');
    if (lbl) lbl.textContent = monthLabel(month);
  }

  timelineToday() {
    const t = todayMonth();
    if (t == null) return;
    this.store.set({ timelineMonth: t });
    this.renderTimeline();
  }

  // --- settings ------------------------------------------------------------
  applySettings() {
    this.renderer.renderGrid(this.store.state, this.store.state.view || { scale: 1, posX: 0, posY: 0 });
    this.renderSnapControl(this.store.state);
  }

  // --- snap to grid ---------------------------------------------------------
  // On/off only (v1's snapToGrid). The step is the minor grid spacing at the
  // current zoom (model/grid), so it's shown on the toggle and follows zooming.
  renderSnapControl(state) {
    if (!this.hasSnapTarget) return;
    const on = !!state.settings?.snapToGrid;
    const step = gridSpacing(state.view?.scale || 1, state.settings?.units).minor;
    this.snapTarget.classList.toggle('is-active', on);
    this.snapTarget.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.snapTarget.textContent = on ? `Snap · ${step?.label || 'off'}` : 'Snap off';
  }

  toggleSnap() { this.toggleSetting('snapToGrid', false); }

  openSettings() {
    if (this.viewOnlyValue) { this.openViewSettings(); return; }
    const s = this.store.state.settings || {};
    const checked = (k) => (s[k] === false ? '' : 'checked');
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Plan settings</h3></header>
        <section class="modal-content">
          <div class="pl2-field"><label>Name</label><input class="pl2-input" type="text" data-s="name" value="${esc(s.name || '')}"></div>
          <div class="pl2-field"><label>Access</label><select class="pl2-input" data-s="access">
            ${['public', 'private', 'link'].map((a) => `<option value="${a}" ${s.access === a ? 'selected' : ''}>${a}</option>`).join('')}
          </select></div>
          <div class="pl2-field"><label>Units</label><select class="pl2-input" data-s="units">
            <option value="metric" ${s.units !== 'imperial' ? 'selected' : ''}>metric</option>
            <option value="imperial" ${s.units === 'imperial' ? 'selected' : ''}>imperial</option>
          </select></div>
          <label class="pl2-check"><input type="checkbox" data-s="showGrid" ${checked('showGrid')}> Show grid</label>
          <label class="pl2-check"><input type="checkbox" data-s="showLabels" ${checked('showLabels')}> Show labels</label>
          <label class="pl2-check"><input type="checkbox" data-s="lockLabels" ${s.lockLabels === true ? 'checked' : ''}> Lock labels (prevent accidental label drags)</label>
          <label class="pl2-check"><input type="checkbox" data-s="autoSave" ${checked('autoSave')}> Auto-save</label>
          ${!this.newValue && this.saveUrlValue ? '<button class="pl2-btn pl2-danger-btn" type="button" data-act="delete" style="margin-top:12px">Delete plan</button>' : ''}
        </section>
        <div class="modal-footer"><button class="btn-primary" data-act="close" type="button">Done</button></div>
      </div>`;
    overlay.querySelectorAll('[data-s]').forEach((inp) => {
      inp.addEventListener('change', () => {
        const val = inp.type === 'checkbox' ? inp.checked : inp.value;
        this.editor.apply(setSetting, { key: inp.dataset.s, value: val });
        this.applySettings();
      });
    });
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.querySelector('[data-act="delete"]')?.addEventListener('click', () => { close(); this.deletePlan(); });
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  // View mode: how *you* see someone's plan — units, grid, labels. Straight
  // into the store, never through the editor: no undo step, never dirty, never
  // saved (markDirty ignores view mode anyway), gone on reload.
  openViewSettings() {
    const s = this.store.state.settings || {};
    const checked = (k) => (s[k] === false ? '' : 'checked');
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>View settings</h3></header>
        <section class="modal-content">
          <p class="pl2-muted">Changes how you see this plan. Nothing is saved.</p>
          <div class="pl2-field"><label>Units</label><select class="pl2-input" data-s="units">
            <option value="metric" ${s.units !== 'imperial' ? 'selected' : ''}>metric</option>
            <option value="imperial" ${s.units === 'imperial' ? 'selected' : ''}>imperial</option>
          </select></div>
          <label class="pl2-check"><input type="checkbox" data-s="showGrid" ${checked('showGrid')}> Show grid</label>
          <label class="pl2-check"><input type="checkbox" data-s="showLabels" ${checked('showLabels')}> Show labels</label>
        </section>
        <div class="modal-footer"><button class="btn-primary" data-act="close" type="button">Done</button></div>
      </div>`;
    overlay.querySelectorAll('[data-s]').forEach((inp) => {
      inp.addEventListener('change', () => {
        const val = inp.type === 'checkbox' ? inp.checked : inp.value;
        this.store.set({ settings: { ...this.store.state.settings, [inp.dataset.s]: val } });
        this.applySettings();
      });
    });
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  // Deleting a plan is irreversible, so ask for its name rather than an OK
  // button one mis-click away (v1 parity).
  deletePlan() {
    const name = (this.store.state.settings?.name || this.store.state.name || '').trim();
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Delete this plan?</h3></header>
        <section class="modal-content">
          <p>This cannot be undone. Type <strong>${esc(name)}</strong> to confirm.</p>
          <input class="input-text pl2-input" type="text" data-del="name" placeholder="${esc(name)}" style="width:100%" autocomplete="off">
        </section>
        <div class="modal-footer">
          <button class="btn-clear-light" data-act="cancel" type="button">Cancel</button>
          <button class="pl2-btn pl2-danger-btn" data-act="ok" type="button" disabled>Delete plan</button>
        </div>
      </div>`;
    const input = overlay.querySelector('[data-del="name"]');
    const ok = overlay.querySelector('[data-act="ok"]');
    const matches = () => input.value.trim().toLowerCase() === name.toLowerCase() && name !== '';
    input.addEventListener('input', () => { ok.disabled = !matches(); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && matches()) ok.click(); });
    ok.addEventListener('click', () => { overlay.remove(); this.destroyPlan(); });
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) overlay.remove(); });
    this.element.appendChild(overlay);
    input.focus();
  }

  async destroyPlan() {
    try {
      const res = await fetch(this.saveUrlValue, { method: 'DELETE', headers: { 'X-CSRF-Token': this.csrf(), Accept: 'application/json' } });
      if (res.ok) {
        // Don't let a queued autosave resurrect the plan we just deleted.
        this.saveCoordinator.cancelTimers();
        window.location.href = `/${this.usernameValue}/plans`;
      } else this.showError('Could not delete the plan.');
    } catch (e) { this.showError('Could not delete the plan.'); }
  }

  syncChrome(state) {
    if (this.hasZoomTarget) this.zoomTarget.textContent = `${Math.round((state.view?.scale || 1) * 100)}%`;
    this.updateJumpButton(state);
    this.renderScaleBar(state);
    this.renderSnapControl(state);
    this.updateTipBar(state);
  }

  // "Jump to content": visible only when the plan has elements but the current
  // viewport shows none of them — clicking recentres the view on the content.
  updateJumpButton(state) {
    if (!this.hasJumpTarget) return;
    const { width, height } = this.renderer.stageSize();
    this.jumpTarget.hidden = !state.loaded || !contentOffscreen(state, width, height);
  }

  jumpToContent() { this.renderer.centerOnContent(); }

  // Slim contextual hint bar at the bottom of the canvas, per active tool.
  updateTipBar(state) {
    if (!this.hasTipbarTarget) return;
    const tips = {
      select: this.selectTip(state),
      navigate: 'Drag to pan · scroll to zoom',
      rectangle: 'Drag to draw a raised bed, patio or building',
      ellipse: 'Drag to draw a tree canopy, pond or round bed',
      polygon: 'Click to add points · double-click to finish — beds, lawns or borders',
      line: 'Draw a fence, hedge, wall, border or path · click to add points, double-click to finish · set its thickness under Thickness',
      erase: 'Click an element to delete it',
      placement: 'Click to place · Shift-click to keep placing · Esc to stop',
      calibrate: 'Click two points a known distance apart',
      addspecies: 'Click elements to add the plant to them · Esc to stop',
      copystyle: 'Click an element to pick up its style, then click others to paint them · Esc to stop',
    };
    const tip = tips[state.activeTool];
    this.tipbarTarget.hidden = !tip;
    if (tip) this.tipbarTarget.textContent = tip;
  }

  // The select tool's hint depends on what's selected — v1 changed its tip the
  // same way, and it's where most of the planner's affordances live.
  selectTip(state) {
    const n = (state.selection || []).length;
    if (n === 0) return 'Click to select · drag a box to select several (⇧ adds, ⌥ removes)';
    if (n > 1) return `${n} elements selected · drag to move them together · Props edits them all at once`;
    const el = state.elements.find((e) => e.id === state.selection[0]);
    if (el && (el.type === 'polygon' || el.type === 'line')) {
      return 'Drag the points to reshape · double-click an edge to add a point (or a point to remove it) · right-click an edge to curve it';
    }
    if (el && el.type === 'ellipse') return 'Drag to move · corner handles resize · right-click for more';
    if (el && el.type === 'image') return 'Drag to move · corner handles resize and rotate · lock it in the Elements tab to trace over it';
    return 'Drag to move · handles resize and rotate · right-click for more';
  }

  // --- help / fullscreen ---------------------------------------------------
  openHelp() {
    window.posthog?.capture('planner_help_opened', { planner_version: 'v2' });
    // What each garden object maps to — the tools only name geometric shapes,
    // so spell out the real-world thing a new gardener is looking for.
    const draw = [
      ['Fence, hedge, wall or border', 'Line tool (L) — click each corner, double-click to finish. Set its thickness under Thickness in the properties panel.'],
      ['Path', 'Line tool (L) — draw the route, then raise the Thickness to the path width.'],
      ['Raised bed, patio or building', 'Rectangle tool (R) — drag a box.'],
      ['Lawn or free-form bed', 'Polygon tool (P) — click each corner, double-click to close.'],
      ['Tree canopy, pond or round bed', 'Ellipse tool (O) — drag a circle.'],
    ];
    const rows = [
      ['V', 'Select'], ['H', 'Pan'], ['R', 'Rectangle'], ['O', 'Ellipse'], ['P', 'Polygon'], ['L', 'Line'], ['E', 'Erase'], ['Y', 'Style eyedropper'],
      ['Space (hold)', 'Pan'], ['⌘Z / ⌘⇧Z', 'Undo / Redo'],
      ['⌘C / ⌘V / ⌘D', 'Copy / Paste / Duplicate — copies travel between tabs and plans'],
      ['⌘A', 'Select all'], ['[ / ]', 'Send backward / forward (⇧ = back/front)'], ['Delete', 'Delete selection'],
      ['Arrows', 'Nudge (⇧ = ×10)'], ['+ / −', 'Zoom in / out'], ['1–6', 'Sidebar tabs'],
      ['⇧G / ⇧L / ⇧S', 'Toggle grid / labels / snap'], ['⇧R', 'Zoom to fit'], ['⌥I / ⌥L', 'Show/hide images / lock labels'],
      ['⇧-drag / ⌥-drag box', 'Add to / remove from selection'], ['⇧ / ⌥ while drawing', 'Square · from centre'],
      ['⌘S', 'Save'], ['Esc', 'Cancel / deselect'],
      ['Double-click path', 'Add a vertex · on a vertex: remove it'],
    ];
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>How do I draw…</h3></header>
        <section class="modal-content">
          <table class="pl2-help">${draw.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>
          <h4 class="pl2-section-title" style="margin-top:16px">Keyboard shortcuts</h4>
          <table class="pl2-help">${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>
        </section>
        <div class="modal-footer"><button class="btn-primary" type="button">Done</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('button').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else this.element.requestFullscreen?.();
  }

  syncFullscreenButton() {
    const on = document.fullscreenElement === this.element;
    this.element.querySelectorAll('.pl2-fullscreen-btn').forEach((btn) => {
      btn.title = on ? 'Exit fullscreen' : 'Fullscreen';
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const use = btn.querySelector('use');
      const href = use?.getAttribute('href');
      if (href) use.setAttribute('href', href.replace(/#fullscreen(-exit)?-line$/, on ? '#fullscreen-exit-line' : '#fullscreen-line'));
    });
  }

  selectAllOfType(type) {
    this.store.set({ selection: this.store.state.elements.filter((e) => e.type === type && !e.data?.locked).map((e) => e.id) });
  }

  // One-time welcome intro, shown on the first planner visit — a new plan or an
  // existing one. Mark it seen only after the modal renders, so a reader who
  // never reaches this point keeps their one chance to see it. View-only readers
  // cannot draw, so they skip it.
  maybeWelcome() {
    if (this.viewOnlyValue) return;
    try { if (localStorage.getItem('pl2.welcomed')) return; } catch (_) { return; }
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Welcome to the planner</h3></header>
        <section class="modal-content">
          <p>Search for plants on the left and click the canvas to place them, or draw your garden with the shape tools up top.</p>
          <p class="pl2-muted">Beds and lawns use the Rectangle or Polygon tool. Fences, hedges, walls, borders and paths all use the <strong>Line</strong> tool — draw the run, then set its thickness under Thickness.</p>
          <p class="pl2-muted">Open <strong>Help</strong> any time for the tool guide and keyboard shortcuts.</p>
        </section>
        <div class="modal-footer"><button class="btn-primary" type="button">Get started</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('button').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
    try { localStorage.setItem('pl2.welcomed', '1'); } catch (_) { /* best-effort: intro is non-critical */ }
  }

  // Resolve a data-species-info reference to a plant object: plain ids hit the
  // species cache; "hit:i" and "list:id:i" reference search results and list
  // rows (whose species aren't cached until placed).
  resolveSpeciesRef(ref) {
    if (ref.startsWith('hit:')) return this._hits[Number(ref.slice(4))];
    if (ref.startsWith('list:')) {
      const [, listId, i] = ref.split(':');
      return this._lists?.[listId]?.plants?.[Number(i)];
    }
    return this.store.state.species[ref];
  }

  // Species-info flyout when hovering any species row (props, plants, search, lists).
  onSpeciesHover(e) {
    this.onSegmentHover(e);
    const row = e.target.closest?.('[data-species-info]');
    if (!row) { this.hideSpeciesFlyout(); return; }
    const sp = this.resolveSpeciesRef(row.dataset.speciesInfo);
    if (!sp) return;
    this.hideSpeciesFlyout();
    const fly = document.createElement('div');
    fly.className = 'pl2-species-flyout';
    fly.innerHTML = `
      ${sp.thumb ? `<img src="${esc(sp.thumb)}" alt="">` : ''}
      <div><div class="pl2-species-flyout__name">${esc(sp.name || '')}</div>
      <div class="pl2-species-flyout__sci">${esc(sp.scientific_name || '')}</div>
      ${sp.parent_name ? `<div class="pl2-muted">${esc(sp.parent_name)}</div>` : ''}
      ${row.dataset.speciesHint ? `<div class="pl2-species-flyout__hint">${esc(row.dataset.speciesHint)}</div>` : ''}</div>`;
    const r = row.getBoundingClientRect();
    fly.style.left = `${r.right + 8}px`;
    fly.style.top = `${r.top}px`;
    // Inside the planner root, not <body>: the design tokens live on .pl2, and
    // in fullscreen only this element is on screen.
    this.element.appendChild(fly);
    this._speciesFlyout = fly;
  }

  hideSpeciesFlyout() { this._speciesFlyout?.remove(); this._speciesFlyout = null; }

  // Instant tooltips for small icon buttons ([data-tip]) — the native `title`
  // tooltip only appears after a second or two.
  onTipHover(e) {
    const target = e.target.closest?.('[data-tip]');
    if (!target) return;
    this.hideTip();
    const tip = document.createElement('div');
    tip.className = 'pl2-tip';
    tip.textContent = target.dataset.tip;
    this.element.appendChild(tip);
    const r = target.getBoundingClientRect();
    const w = tip.offsetWidth;
    tip.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2))}px`;
    tip.style.top = `${r.bottom + 6}px`;
    this._tip = tip;
  }

  hideTip() { this._tip?.remove(); this._tip = null; }

  // Scale bar (1 plan unit = 1 cm), ported from v1's grid legend. It reads
  // the grid's own minor/major spacing, so it names what's drawn (and snapped to).
  renderScaleBar(state) {
    if (!this.hasScalebarTarget) return;
    const { minor, major } = gridSpacing(state.view?.scale || 1, state.settings?.units);
    if (!major) { this.scalebarTarget.hidden = true; return; }
    const ticks = Math.max(1, Math.round(major.abs / minor.abs));
    const minorPx = major.px / ticks; // exact, no drift
    const W = major.px;
    let lines = '';
    for (let i = 0; i <= ticks; i += 1) {
      const x = (i * minorPx + 0.5).toFixed(2);
      const tall = i === 0 || i === ticks;
      lines += `<line x1="${x}" y1="${tall ? 1 : 4}" x2="${x}" y2="10"/>`;
    }
    lines += `<line x1="0.5" y1="10" x2="${(W + 0.5).toFixed(2)}" y2="10"/>`;
    this.scalebarTarget.hidden = false;
    this.scalebarTarget.innerHTML = `
      <svg class="pl2-scalebar__svg" width="${Math.ceil(W) + 2}" height="12" shape-rendering="crispEdges">${lines}</svg>
      <span>${major.label} / ${minor.label}</span>`;
  }

  // --- side panels (markup + events live in panels/) ------------------------
  refreshPanels(state = this.store.state) { renderPanels(this.element, state, this); }

  onPanelClick(e) {
    // Close the zoom menu when clicking outside the zoom control.
    if (this.hasZoomMenuTarget && !this.zoomMenuTarget.hidden && !e.target.closest('.pl2-zoomctl')) {
      this.zoomMenuTarget.hidden = true;
    }
    handlePanelClick(e, this);
  }

  // Lazy-load the user's plant lists the first time the tab opens.
  async loadLists() {
    if (this._listsLoaded || this._listsLoading || !this.usernameValue) return;
    this._listsLoading = true;
    this.refreshPanels();
    try {
      const res = await fetch(`/${this.usernameValue}/plans/lists`, { headers: { Accept: 'application/json' } });
      this._lists = res.ok ? await res.json() : {};
    } catch (e) { this._lists = {}; }
    this._listsLoaded = true; this._listsLoading = false;
    this.refreshPanels();
  }

  // --- panel-context accessors used by panels/index.js ----------------------
  imageSize(el) { return el.type === 'image' ? this.renderer.naturalImageSize(el.data?.url) : null; }
  searchHit(i) { return this._hits[i]; }
  listPlant(listId, i) { return this._lists?.[listId]?.plants?.[i]; }

  addSpeciesToSelected(plant) {
    const el = this.selectedElement();
    if (el) this.editor.apply(addSpecies, { elementId: el.id, plant });
  }

  // Hovering an Elements-tab row outlines that element on the canvas; a
  // Plants-tab row outlines every element carrying the plant (within the
  // tab's layer filter). Locked elements stay inert, as on the canvas.
  onRowHover(e) {
    const row = e.target.closest?.('[data-tab-panel="elements"] [data-pl2="element-select"], [data-tab-panel="plants"] [data-pl2="plant-select"]');
    if (!row) {
      if (this._rowHover) { this._rowHover = false; this.renderer.setHover(null); }
      return;
    }
    const state = this.store.state;
    const els = row.dataset.pl2 === 'element-select'
      ? state.elements.filter((el) => el.id === row.dataset.id)
      : plantElements(state, this.filters?.plantsLayer).filter((el) => (el.speciesIds || []).includes(Number(row.dataset.id)));
    this._rowHover = true;
    this.renderer.setHover(els.filter((el) => !el.data?.locked));
  }

  // Info-tab Dimensions rows: hovering "Segment n" highlights that segment.
  onSegmentHover(e) {
    const row = e.target.closest?.('[data-segment]');
    if (!row) { if (this._segHover) { this._segHover = false; this.renderer.setHover(null); } return; }
    const el = this.store.state.elements.find((x) => x.id === row.dataset.id);
    this._segHover = true;
    this.renderer.setSegmentHover(el, Number(row.dataset.segment));
  }

  // View mode: clicking a row selects the element(s) and, only when none of
  // them is fully on screen, centres the view on them (zoom unchanged).
  focusElements(ids) {
    const els = this.store.state.elements.filter((x) => ids.includes(x.id));
    const rects = els.map(elementRect).filter(Boolean);
    const patch = { selection: els.map((el) => el.id) };
    if (rects.length) {
      const v = this.store.state.view;
      const scale = v.scale || 1;
      const w = this.renderer.stage.width(); const h = this.renderer.stage.height();
      const onScreen = (r) => r.minX * scale + v.posX >= 0 && r.maxX * scale + v.posX <= w
        && r.minY * scale + v.posY >= 0 && r.maxY * scale + v.posY <= h;
      if (!rects.some(onScreen)) {
        // Centre on the whole group when it fits, else on the first element.
        const union = rects.reduce((a, r) => ({ minX: Math.min(a.minX, r.minX), minY: Math.min(a.minY, r.minY), maxX: Math.max(a.maxX, r.maxX), maxY: Math.max(a.maxY, r.maxY) }));
        const fits = (union.maxX - union.minX) * scale <= w && (union.maxY - union.minY) * scale <= h;
        const t = fits ? union : rects[0];
        const cx = (t.minX + t.maxX) / 2; const cy = (t.minY + t.maxY) / 2;
        patch.view = { ...v, scale, posX: w / 2 - cx * scale, posY: h / 2 - cy * scale };
      }
    }
    this.store.set(patch);
  }

  openSpeciesSearch(onPick) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Add a plant</h3></header>
        <section class="modal-content">
          <input class="pl2-input" type="search" placeholder="Search plants…" data-sp="input" style="width:100%">
          <div class="pl2-search-results" data-sp="results" style="margin-top:8px"></div>
        </section>
        <div class="modal-footer"><button class="btn-clear-light" data-act="close" type="button">Cancel</button></div>
      </div>`;
    const close = () => overlay.remove();
    const input = overlay.querySelector('[data-sp="input"]');
    const results = overlay.querySelector('[data-sp="results"]');
    let hits = []; let t;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      t = setTimeout(async () => {
        try { const res = await searchPlants({ q, limit: 20 }); hits = res.hits || []; } catch (e) { hits = []; }
        results.innerHTML = hits.map((h, i) => `<div class="pl2-result" data-i="${i}">${h.thumb ? `<img class="pl2-result__img" src="${esc(h.thumb)}" alt="">` : '<span class="pl2-result__img"></span>'}<span class="pl2-result__text"><span class="pl2-result__name">${esc(h.name || '')}</span><span class="pl2-result__sci">${esc(h.scientific_name || '')}</span></span></div>`).join('');
      }, 180);
    });
    results.addEventListener('click', (e) => {
      const row = e.target.closest('[data-i]');
      if (!row) return;
      const plant = hits[Number(row.dataset.i)];
      if (plant) { onPick(plant); close(); }
    });
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
    input.focus();
  }

  selectedElement() {
    const sel = this.store.state.selection;
    const element = sel.length === 1 ? this.store.state.elements.find((e) => e.id === sel[0]) : null;
    return element?.data?.locked ? null : element;
  }

  // --- plant search + placement --------------------------------------------
  search(event) {
    const q = event.target.value.trim();
    clearTimeout(this._searchT);
    if (!q) { this.searchResultsTarget.innerHTML = ''; this._hits = []; this._searchQuery = null; return; }
    this._searchT = setTimeout(() => this.runSearch(q), 180);
  }

  // offset > 0 appends the next page to the existing hits (Load more).
  async runSearch(q, offset = 0) {
    try {
      const res = await searchPlants({ q, limit: SEARCH_PAGE, offset });
      this._searchQuery = q;
      this._searchTotal = res.estimatedTotalHits ?? null;
      this._hits = offset ? [...this._hits, ...(res.hits || [])] : (res.hits || []);
      this.searchResultsTarget.innerHTML = searchResultsHtml(this._hits, { total: this._searchTotal });
    } catch (e) {
      this.searchResultsTarget.innerHTML = '<p class="pl2-muted">Search unavailable.</p>';
    }
  }

  loadMoreSearch() {
    if (this._searchQuery) this.runSearch(this._searchQuery, this._hits.length);
  }

  // Properties shows what the plant will be placed with (appearance, layer,
  // dates), the layer preset to the plant's own when the plan has it — v1's
  // setActiveLayerFromSpecies, which also leaves it picked for later tools.
  startPlacement(species) {
    this.store.state.species = { ...this.store.state.species, [species.id]: species };
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    const drawStyles = placementStyles(this.store.state.drawStyles, species.id, speciesLayer(species, this.store.state.layers));
    this.store.set({ activeTool: 'placement', pendingSpecies: species, selection: [], drawStyles });
    this.renderer.setDraggable(false);
    this.syncToolButtons();
    if (!this.viewOnlyValue) this.showTab('properties');
    this.closePanel(); // mobile: get out of the way so the canvas is tappable
  }

  placeSpecies(x, y, sticky = false) {
    const species = this.store.state.pendingSpecies;
    if (!species) return;
    const el = makePlantElement(species, x, y, this.store.state.layers);
    const { canopyStyle, canopySeed, ...plant } = el.data;
    el.data = { ...plant, ...drawStyleData(this.store.state.drawStyles, 'placement', canopySeed) };
    // Default: drop the plant, then return to select so it can be moved/edited.
    // Hold Shift to stay in placement and drop several (matches v1).
    this.editor.apply(addElements, { elements: [el], select: !sticky });
    if (!sticky) this.applyTool('select');
  }

  // --- keyboard -------------------------------------------------------------
  onKey(e) {
    // An open modal owns the keyboard: Esc closes it (through its own backdrop
    // handler, so each modal cleans up as it would on a click outside) and no
    // planner shortcut fires behind it. Hidden overlays (the Classic-switch
    // modal lives in the page until opened) don't count.
    const modal = [...this.element.querySelectorAll('.modal-overlay:not([hidden])')].pop();
    if (modal) {
      if (e.key === 'Escape') {
        e.preventDefault();
        modal.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      }
      return;
    }
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
    const mod = e.metaKey || e.ctrlKey;

    if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); this.save(); return; }
    if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); return; }
    if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); this.redo(); return; }
    if (mod && e.key.toLowerCase() === 'c') { e.preventDefault(); this.copySelection(); return; }
    if (mod && e.key.toLowerCase() === 'v') { e.preventDefault(); this.pasteClipboard(); return; }
    if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); this.cloneSelection(); return; }
    if (mod && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      this.store.set({ selection: this.store.state.elements.filter((el) => !el.data?.locked).map((el) => el.id) });
      return;
    }
    if (e.key === ']') { e.preventDefault(); e.shiftKey ? this.bringToFront() : this.bringForward(); return; }
    if (e.key === '[') { e.preventDefault(); e.shiftKey ? this.sendToBack() : this.sendBackward(); return; }
    if (e.key === '+' || e.key === '=') { e.preventDefault(); this.zoomIn(); return; }
    if (e.key === '-') { e.preventDefault(); this.zoomOut(); return; }

    // Hold Space to pan; releasing restores the previous tool (v1 parity).
    if (e.key === ' ' && !e.repeat && this._spaceTool == null) {
      e.preventDefault();
      this._spaceTool = this.store.state.activeTool;
      this.applyTool('navigate');
      return;
    }

    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.deleteSelection(); return; }
    if (e.key === 'Escape') {
      this.interactions.cancelPoly(); this.renderer.clearPreview();
      const tool = this.store.state.activeTool;
      if (tool === 'calibrate') { this.interactions._calib = null; this.applyTool('select'); }
      else if (['placement', 'addspecies', 'copystyle'].includes(tool)) this.applyTool('select');
      else if (this.store.state.selectedSegment) this.store.set({ selectedSegment: null }); // a picked segment first (v1)
      else this.store.set({ selection: [] });
      return;
    }
    if (e.key === 'Enter') { this.interactions.finishPoly?.(); return; }
    if (e.key === '?') { e.preventDefault(); this.openHelp(); return; }

    // SHIFT/ALT setting toggles + reset view (v1 keys).
    if (e.shiftKey && !mod) {
      const k = e.key.toLowerCase();
      if (k === 'g') { e.preventDefault(); this.toggleSetting('showGrid'); return; }
      if (k === 'l') { e.preventDefault(); this.toggleSetting('showLabels'); return; }
      if (k === 's') { e.preventDefault(); this.toggleSnap(); return; }
      if (k === 'r') { e.preventDefault(); this.renderer.fitToContent(); return; }
    }
    if (e.altKey && !mod) {
      const k = e.key.toLowerCase();
      if (k === 'i') { e.preventDefault(); this.toggleImages(); return; }
      if (k === 'l') { e.preventDefault(); this.toggleSetting('lockLabels', false); return; }
    }

    // 1–6 jump to the sidebar tabs (v1's focus keys, without tool-key clashes).
    const tabs = Array.from(this.element.querySelectorAll('.pl2-tab[data-tab]'));
    const tabIdx = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 }[e.key];
    if (tabIdx != null && !mod && tabs[tabIdx]) { e.preventDefault(); tabs[tabIdx].click(); return; }

    const nudge = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    if (nudge) { e.preventDefault(); this.nudge(nudge[0] * (e.shiftKey ? 10 : 1), nudge[1] * (e.shiftKey ? 10 : 1)); return; }

    if (e.key.toLowerCase() === 'y' && !mod) { this.startCopyStyle(); return; }
    const toolKey = { v: 'select', h: 'navigate', r: 'rectangle', o: 'ellipse', p: 'polygon', l: 'line', e: 'erase' }[e.key.toLowerCase()];
    if (toolKey && !mod) this.applyTool(toolKey, 'user');
  }

  // Alt+I: hide every image if any is showing, else show them all.
  toggleImages() {
    const images = this.store.state.elements.filter((el) => el.type === 'image');
    if (!images.length) return;
    this.editor.apply(setImagesVisible, { visible: !images.some((el) => el.data?.visible !== false) });
  }

  // Flip a boolean setting through the editor (undoable, persisted, applied).
  // `onDefault` is what an unset value means (true for show*, false for locks).
  toggleSetting(key, onDefault = true) {
    const cur = this.store.state.settings?.[key] ?? onDefault;
    this.editor.apply(setSetting, { key, value: !cur });
    this.applySettings();
  }

  onKeyUp(e) {
    if (e.key === ' ' && this._spaceTool != null) {
      const tool = this._spaceTool;
      this._spaceTool = null;
      this.applyTool(tool);
    }
  }

  deleteSelection() {
    this.editor.apply(deleteElements, { ids: this.store.state.selection });
  }

  nudge(dx, dy) {
    this.editor.apply(nudgeElements, { ids: this.store.state.selection, dx, dy });
  }

  // --- clipboard / duplicate -----------------------------------------------
  selectedElements() {
    const sel = new Set(this.store.state.selection);
    return this.store.state.elements.filter((el) => sel.has(el.id) && !el.data?.locked);
  }

  // Copy through the system clipboard so elements can travel between tabs and
  // plans (v1 parity); the in-session copy is kept as a fallback for browsers
  // that refuse clipboard access.
  copySelection() {
    const els = this.selectedElements().map(({ id, ...el }) => JSON.parse(JSON.stringify(el)));
    if (!els.length) return;
    this.clipboard = els;
    navigator.clipboard?.writeText?.(encodeElements(els)).catch(() => { /* in-session copy stands */ });
  }

  async pasteClipboard() {
    let els;
    try {
      // A readable clipboard is the source of truth: if it holds something
      // that isn't ours, paste nothing rather than resurrecting an older
      // in-session copy the user has since moved on from.
      els = decodeElements(await navigator.clipboard.readText());
    } catch (_) {
      els = this.clipboard; // denied or unsupported — in-session copy stands in
    }
    if (els?.length) this.duplicateElements(els);
  }

  cloneSelection() {
    const els = this.selectedElements().map(({ id, ...el }) => JSON.parse(JSON.stringify(el)));
    if (els.length) this.duplicateElements(els);
  }

  duplicateElements(elems, offset = 20) {
    const created = elems.map((el, i) => {
      const copy = JSON.parse(JSON.stringify(el));
      copy.id = `el-${Date.now()}-${i}-${Math.round(Math.random() * 1e4)}`;
      offsetElement(copy, offset, offset);
      return copy;
    });
    this.editor.apply(addElements, { elements: created });
  }

  // --- z-order --------------------------------------------------------------
  bringToFront() { this.reorder('front'); }
  sendToBack() { this.reorder('back'); }
  bringForward() { this.reorder('forward'); }
  sendBackward() { this.reorder('backward'); }

  reorder(dir) {
    this.editor.apply(reorderElements, { ids: this.store.state.selection, dir });
  }

  // --- copy / paste style ---------------------------------------------------
  copyStyle() {
    const el = this.selectedElement();
    if (!el) return;
    const d = el.data || {};
    this.styleClipboard = { fillColor: d.fillColor, strokeColor: d.strokeColor, strokeWidth: d.strokeWidth, canopyStyle: d.canopyStyle };
  }

  // --- eyedropper + sticky add-species canvas tools (v1 parity) -------------
  startCopyStyle() {
    this.pickedStyle = null;
    this.applyTool('copystyle');
    this.showHint('Click an element to pick up its style (Esc to stop).');
  }

  pickStyleFrom(el) {
    const d = el.data || {};
    this.pickedStyle = { fillColor: d.fillColor, strokeColor: d.strokeColor, strokeWidth: d.strokeWidth, canopyStyle: d.canopyStyle };
    this.styleClipboard = this.pickedStyle; // context-menu Paste style shares it
    this.showHint('Style copied — click elements to paint them (Esc to stop).');
  }

  // Click element after element to attach `plant` to each.
  startAddSpecies(plant) {
    this.store.state.species = { ...this.store.state.species, [plant.id]: plant };
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    this.store.set({ activeTool: 'addspecies', pendingSpecies: plant });
    this.renderer.setDraggable(false);
    this.canvasTarget.dataset.tool = 'addspecies';
    this.syncToolButtons();
    this.closePanel();
    this.showHint(`Click elements to add ${plant.name || 'the plant'} to them (Esc to stop).`);
  }

  toggleTouchMultiSelect() {
    const on = !this.store.state.touchMultiSelect;
    this.store.state.touchMultiSelect = on;
    this.element.querySelector('[data-action="planner#toggleTouchMultiSelect"]')?.classList.toggle('is-active', on);
  }

  pasteStyle() {
    if (!this.styleClipboard) return;
    this.editor.apply(pasteStyle, { ids: this.store.state.selection, style: this.styleClipboard });
  }

  // --- right-click context menu --------------------------------------------
  // Route a right-click/long-press through disambiguation: when several
  // elements overlap under the pointer, offer a chooser first (v1
  // SelectionMenu). If everything under the pointer is already selected, go
  // straight to the actions menu so multi-selection workflows keep working.
  // Right-click (or long-press) with `hits` — every element under the
  // pointer, topmost first. One menu: the actions apply to the topmost element
  // (what a left-click would select) — or to the selection when every hit is
  // already in it — and, where elements overlap, they're listed at the top to
  // switch to. View mode has no actions: just the list, and a lone hit is
  // simply selected and shown in Info.
  openContextForHits(clientX, clientY, hits, planPos = null) {
    this._ctxPlanPos = planPos;
    if (this.viewOnlyValue) {
      if (hits.length > 1) this.openContextMenu(clientX, clientY, null, { hits });
      else if (hits.length === 1) this.selectAndShowInfo(hits[0]);
      return;
    }
    const sel = new Set(this.store.state.selection);
    const onSelection = hits.length > 0 && hits.every((el) => sel.has(el.id));
    this.openContextMenu(clientX, clientY, onSelection ? null : (hits[0] || null), { hits: hits.length > 1 ? hits : [] });
  }

  // A selection made on the canvas brings up its properties. Selecting from a
  // sidebar list doesn't — that would pull the user away from the list.
  showPropertiesForSelection() {
    if (this.viewOnlyValue || !this.store.state.selection.length) return;
    if (this.store.state.activeTab !== 'properties') this.showTab('properties');
  }

  selectAndShowInfo(el) {
    this.store.set({ selection: [el.id] });
    this.showTab('info');
  }

  // Keep a popup menu fully on screen: flip it up/left when it would overflow,
  // and cap its height (with scrolling) when it is taller than the viewport.
  placeMenu(menu, clientX, clientY) {
    menu.style.left = `${clientX}px`;
    menu.style.top = `${clientY}px`;
    const { width, height } = menu.getBoundingClientRect();
    const margin = 8;
    const maxH = window.innerHeight - margin * 2;
    if (height > maxH) {
      menu.style.maxHeight = `${maxH}px`;
      menu.style.overflowY = 'auto';
    }
    const h = Math.min(height, maxH);
    if (clientY + h > window.innerHeight - margin) {
      menu.style.top = `${Math.max(margin, Math.min(clientY - h, window.innerHeight - h - margin))}px`;
    }
    if (clientX + width > window.innerWidth - margin) {
      menu.style.left = `${Math.max(margin, clientX - width)}px`;
    }
  }

  // `el`: the element the actions apply to (selected first), or null for the
  // current selection. `hits`: overlapping elements to list at the top —
  // hovering one outlines it, clicking one selects it and keeps the menu open
  // (rebuilt for it), Shift-clicking toggles it in the selection. `vertex`:
  // { id, index } when the right-click landed on a point of the selected
  // path — the actions then lead with "Remove point".
  openContextMenu(clientX, clientY, el, { vertex = null, hits = [] } = {}) {
    this.closeContextMenu();
    if (el?.data?.locked) return;
    if (el && !this.store.state.selection.includes(el.id)) {
      this.store.set({ selection: [el.id] });
      if (this.viewOnlyValue) this.showTab('info');
      else this.showPropertiesForSelection();
    }
    const state = this.store.state;
    const species = state.species;
    const sel = state.selection;
    const hasSel = sel.length > 0;
    const menu = document.createElement('div');
    menu.className = 'pl2-ctxmenu';
    const title = this.viewOnlyValue ? 'Elements here'
      : sel.length > 1 ? `${sel.length} elements`
        : sel.length ? elementLabel(state.elements.find((x) => x.id === sel[0]) || {}, species) : 'Plan';
    const head = `<div class="pl2-ctxmenu__head"><span class="pl2-ctxmenu__title">${esc(title)}</span>
      <button class="pl2-ctxmenu__close" type="button" data-ctx-close aria-label="Close menu" title="Close (Esc)">×</button></div>`;
    const list = hits.length ? `<div class="pl2-ctxmenu__hits" role="group" aria-label="Elements here">${hits.map((h, i) => `
      <button type="button" data-hit="${i}"${sel.includes(h.id) ? ' aria-current="true"' : ''}>
        <span class="pl2-row__swatch" style="background:${esc(h.data?.fillColor || h.data?.strokeColor || '#ccc')}"></span>
        <span class="pl2-ctxmenu__name">${esc(elementLabel(h, species))}</span>
      </button>`).join('')}</div>` : '';
    const item = (label, action, disabled) => `<button type="button" data-ctx="${action}"${disabled ? ' disabled' : ''}>${label}</button>`;
    let actions = '';
    if (!this.viewOnlyValue) {
      const isPath = !!this.singleSelectedPath();
      const anchors = vertex ? (el?.data?.coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number').length : 0;
      actions = [
        // A path keeps at least two points.
        vertex ? item('Remove point', 'remove-point', anchors <= 2) + '<hr>' : '',
        item('Duplicate', 'clone', !hasSel),
        item('Copy', 'copy', !hasSel),
        // Always enabled: the system clipboard can hold elements copied in
        // another tab, and we can't inspect it synchronously.
        item('Paste', 'paste', false),
        item('Delete', 'delete', !hasSel),
        '<hr>',
        item('Bring to front', 'front', !hasSel),
        item('Send to back', 'back', !hasSel),
        '<hr>',
        item('Copy style', 'copy-style', sel.length !== 1),
        item('Paste style', 'paste-style', !this.styleClipboard || !hasSel),
        isPath ? '<hr>' + item('Smooth (curves)', 'smooth', false) + item('Straighten', 'straighten', false) : '',
        // Per-segment edits when the right-click landed on a particular edge.
        isPath && this.contextSegment() != null
          ? item('Smooth this segment', 'smooth-seg', false) + item('Straighten this segment', 'straighten-seg', false)
          : '',
      ].join('');
    }
    this._ctxVertex = vertex;
    menu.innerHTML = head + list + (list && actions ? '<hr>' : '') + actions;
    menu.addEventListener('mouseover', (e) => {
      const b = e.target.closest('[data-hit]');
      if (b) this.renderer.setHover(hits[Number(b.dataset.hit)]);
    });
    menu.addEventListener('mouseout', (e) => { if (e.target.closest('[data-hit]')) this.renderer.setHover(null); });
    menu.addEventListener('click', (e) => {
      if (e.target.closest('[data-ctx-close]')) { this.closeContextMenu(); return; }
      const hit = e.target.closest('[data-hit]');
      if (hit) {
        const target = hits[Number(hit.dataset.hit)];
        if (target?.data?.locked) return;
        // Shift (or ⌘/Ctrl) toggles it in the selection, like on the canvas.
        const current = this.store.state.selection;
        const additive = !this.viewOnlyValue && (e.shiftKey || e.metaKey || e.ctrlKey);
        const selection = !additive ? [target.id]
          : current.includes(target.id) ? current.filter((id) => id !== target.id) : [...current, target.id];
        // Toggling in the menu keeps it open, even when nothing's left selected.
        this._ctxMenuSelected = false;
        this.store.set({ selection });
        if (this.viewOnlyValue) this.showTab('info');
        else this.showPropertiesForSelection();
        // Rebuilt in place for the new selection; the pointer stays on its row.
        const { left, top } = menu.style;
        this.openContextMenu(clientX, clientY, null, { hits });
        if (this._ctxMenu) { this._ctxMenu.style.left = left; this._ctxMenu.style.top = top; }
        this.renderer.setHover(target);
        return;
      }
      const b = e.target.closest('[data-ctx]');
      if (!b || b.disabled) return;
      this.runCtx(b.dataset.ctx);
      this.closeContextMenu();
    });
    this.element.appendChild(menu); // inside .pl2 so the CSS vars resolve
    this.placeMenu(menu, clientX, clientY);
    this._ctxMenu = menu;
    // Opened on a selection, it closes when that selection is cleared.
    this._ctxMenuSelected = hasSel;
    this._ctxClose = (e) => { if (!menu.contains(e.target)) this.closeContextMenu(); };
    setTimeout(() => document.addEventListener('mousedown', this._ctxClose), 0);
  }

  closeContextMenu() {
    if (!this._ctxMenu) return;
    this._ctxMenu.remove();
    this._ctxMenu = null;
    this.renderer?.setHover(null);
    if (this._ctxClose) { document.removeEventListener('mousedown', this._ctxClose); this._ctxClose = null; }
  }

  runCtx(action) {
    const map = {
      clone: () => this.cloneSelection(), copy: () => this.copySelection(), paste: () => this.pasteClipboard(),
      delete: () => this.deleteSelection(), front: () => this.bringToFront(), back: () => this.sendToBack(),
      'copy-style': () => this.copyStyle(), 'paste-style': () => this.pasteStyle(),
      smooth: () => this.smoothSelection(), straighten: () => this.straightenSelection(),
      'smooth-seg': () => this.smoothContextSegment(), 'straighten-seg': () => this.straightenContextSegment(),
      'remove-point': () => { if (this._ctxVertex) this.editor.apply(removeVertex, this._ctxVertex); },
    };
    (map[action] || (() => {}))();
  }

  // The coordinate index of the path segment under the last right-click, or
  // null when the click wasn't near one (or nothing path-like is selected).
  contextSegment() {
    const el = this.singleSelectedPath();
    const pos = this._ctxPlanPos;
    if (!el || !pos) return null;
    const hit = nearestSegment(el.data.coordinates || [], el.type === 'polygon', pos.x, pos.y);
    if (!hit || hit.seg.implicit) return null;
    // Only offer it when the click was genuinely near the edge (a few screen px).
    const tolerance = 12 / (this.store.state.view.scale || 1);
    return hit.distance <= tolerance ? hit.seg.index : null;
  }

  smoothContextSegment() {
    const el = this.singleSelectedPath(); const index = this.contextSegment();
    if (el && index != null) this.editor.apply(smoothSegment, { id: el.id, index });
  }

  straightenContextSegment() {
    const el = this.singleSelectedPath(); const index = this.contextSegment();
    if (el && index != null) this.editor.apply(straightenSegment, { id: el.id, index });
  }

  // The selected element if it's a single polygon/line, else null.
  singleSelectedPath() {
    const sel = this.store.state.selection;
    if (sel.length !== 1) return null;
    const el = this.store.state.elements.find((e) => e.id === sel[0]);
    return el && (el.type === 'polygon' || el.type === 'line') ? el : null;
  }

  smoothSelection() {
    const el = this.singleSelectedPath();
    if (el) this.editor.apply(smoothPath, { id: el.id });
  }

  straightenSelection() {
    const el = this.singleSelectedPath();
    if (el) this.editor.apply(straightenPath, { id: el.id });
  }

  // --- error modal ----------------------------------------------------------
  // An expired session can only be fixed by reloading, so its OK button
  // becomes Refresh (the latest edits are kept in the local draft first).
  showError(message, sessionExpired = false, title = "Couldn't save your plan") {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>${esc(title)}</h3></header>
        <section class="modal-content"><p>${esc(message)}</p></section>
        <div class="modal-footer"><button class="btn-primary" type="button">${sessionExpired ? 'Refresh' : 'OK'}</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('button').addEventListener('click', sessionExpired ? () => this.refreshWithDraft() : close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }
}

